/**
 * tourTheme.js
 *
 * Design tokens extracted from the "Master Tour Calendar" design system
 * (Tour Calendar.dc.html + Routing Map.html).
 *
 * WHY THIS FILE EXISTS
 * The tour-planner UI is spread across TourPlanner.js (18k lines),
 * MapSideRail.js, RightMapToursRail.js and FundsPanel.js, each carrying its
 * own hard-coded hex values. Every new panel drifts a little further from the
 * last. Import from here instead so the palette lives in exactly one place.
 *
 * IMPORTANT — oklch() does not work in React Native.
 * The source design used oklch() for its four status colours. Those are
 * converted to sRGB hex below; the original oklch string is kept in a comment
 * so the web design and the app stay traceable to each other.
 */

// ─────────────────────────────────────────────────────────────
// Palette — mirrors the `PAL` object at line 538 of Tour Calendar.dc.html
// ─────────────────────────────────────────────────────────────
export const PAL = {
  ink: '#14141a', // primary text, active fills, route lines
  inkSoft: '#23232a', // raised ink — secondary headings
  mute: '#7a7a75', // secondary/label text
  muteSoft: '#8a8a85', // tertiary text
  idle: '#9a9a94', // disabled / placeholder
  faint: '#a8a8a2', // hairline text, timestamps
  line: '#e4e4df', // ALL 1px borders — 17 of 17 borders in the source use this
  lineSoft: '#f0f0eb', // internal dividers inside a card
  surf: '#fffffe', // card / panel surface
  page: '#f4f4f2', // app background (warm off-white, not pure grey)
  sunk: '#eeeee9', // inset wells, track backgrounds
  emphasis: '#e6e6e1', // spotlight panel — gray vs white cards, still in light theme
  emphasisWell: '#fffffe', // nested input on an emphasis panel
  emphasisLine: '#d0d0ca', // dividers on the gray spotlight

  // Status — converted from oklch()
  ok: '#3f8f5f', // oklch(0.58 0.12 150) — confirmed, on-track
  warn: '#c08133', // oklch(0.62 0.14 65)  — travel/fly legs, attention
  fridge: '#e8a43d', // Canva p9 — fridge / pantry pin gold
  alert: '#c0483c', // oklch(0.55 0.17 28)  — conflict, over-capacity
  info: '#5a6fa8', // oklch(0.55 0.12 265) — informational
  link: '#4a6396', // oklch(0.52 0.11 250) — anchors
  linkHover: '#3a4f7a', // oklch(0.42 0.11 250)
  teal: '#2a7d5c', // Master Tour Calendar v2 brand (afternoon design)
};

// Dark-mode counterparts. The source design is light-only; these keep the same
// hue relationships so the app's existing dark toggle doesn't break.
export const PAL_DARK = {
  ink: '#f4f4f2',
  inkSoft: '#e4e4df',
  mute: '#9a9a94',
  muteSoft: '#8a8a85',
  idle: '#6a6a65',
  faint: '#5a5a55',
  line: 'rgba(255,255,255,0.12)',
  lineSoft: 'rgba(255,255,255,0.07)',
  surf: '#1b1b21',
  page: '#131318',
  sunk: '#23232a',
  emphasis: '#2a2a33', // spotlight panel — lifted charcoal vs page/cards, still in dark theme
  emphasisWell: '#1b1b21', // nested input on an emphasis panel
  emphasisLine: 'rgba(255,255,255,0.12)',
  ok: '#5fb37f',
  warn: '#d9a05c',
  alert: '#d97066',
  info: '#8296c4',
  link: '#8fa4d4',
  linkHover: '#aebde4',
  teal: '#3aa87a',
};

export const palette = (isDarkMode) => (isDarkMode ? PAL_DARK : PAL);

// ─────────────────────────────────────────────────────────────
// Type — Tour Calendar.dc.html: Space Grotesk + IBM Plex Mono.
// ─────────────────────────────────────────────────────────────
export const FONT = {
  sans: "'Space Grotesk', Helvetica, sans-serif",
  mono: "'IBM Plex Mono', monospace",
};

export const TYPE = {
  // Content — ~12% larger than the Claude HTML so the live calendar is easier to read
  title: { fontFamily: FONT.sans, fontSize: 20, fontWeight: '700', letterSpacing: -0.36 },
  heading: { fontFamily: FONT.sans, fontSize: 16, fontWeight: '600', letterSpacing: -0.15 },
  body: { fontFamily: FONT.sans, fontSize: 14.5, fontWeight: '400' },
  bodySm: { fontFamily: FONT.sans, fontSize: 13, fontWeight: '400' },

  // Labels — uppercase + wide tracking is the house signature (0.13em dominates)
  label: { fontFamily: FONT.mono, fontSize: 11.5, letterSpacing: 1.3, textTransform: 'uppercase' },
  labelSm: { fontFamily: FONT.mono, fontSize: 10, letterSpacing: 1.0, textTransform: 'uppercase' },
  meta: { fontFamily: FONT.mono, fontSize: 12, letterSpacing: 0.7 },
  metric: { fontFamily: FONT.mono, fontSize: 14.5, fontWeight: '600', letterSpacing: 0.4 },
  chip: { fontFamily: FONT.mono, fontSize: 11, letterSpacing: 0.6 },
};

// ─────────────────────────────────────────────────────────────
// Geometry — radii cluster tightly in the source; don't invent new ones
// ─────────────────────────────────────────────────────────────
export const RADIUS = {
  card: 12,
  panel: 8,
  control: 6,
  chip: 5,
  tag: 3,
  pill: 42,
  round: 999,
};

export const SPACE = { xs: 4, sm: 6, md: 8, lg: 12, xl: 14, xxl: 24 };

export const BORDER = (isDarkMode) => ({
  borderWidth: 1,
  borderColor: palette(isDarkMode).line,
});

// Shadows are near-absent in the source — separation comes from the 1px border.
// This is deliberately subtle; resist deepening it.
export const ELEVATION = {
  panel: {
    shadowColor: '#14141a',
    shadowOpacity: 0.06,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  none: { shadowOpacity: 0, elevation: 0 },
};

// ─────────────────────────────────────────────────────────────
// Map styling — Routing Map.html
// Route legs: solid ink for ground travel, dashed warn for flights.
// ─────────────────────────────────────────────────────────────
export const MAP = {
  driveColor: PAL.ink,
  driveWidth: 2.5,
  flyColor: PAL.warn,
  flyWidth: 2,
  flyDash: [5, 6],
  legOpacity: 0.75,
  stopSize: 22,
  stopRing: 'rgba(244,244,242,0.9)', // 3px halo so pins read over any basemap
  fitPadding: 50,
};

// Category colours for non-venue map layers. Deliberately drawn from the
// status ramp rather than new hues, so added layers stay in-family.
export const CANVA_FRIDGE_GOLD = '#e8a43d';

export const LAYER_COLOR = {
  resource: PAL.info,
  travel: PAL.ink,
  farm: PAL.ok, // pickup
  foodBank: CANVA_FRIDGE_GOLD, // Canva p9 fridge color
  transit: PAL.info,
  flag: PAL.alert,
  flyer: PAL.warn, // Event Flyer choice in Add to Map (REQ-065)
};

export default {
  PAL,
  PAL_DARK,
  palette,
  FONT,
  TYPE,
  RADIUS,
  SPACE,
  BORDER,
  ELEVATION,
  MAP,
  LAYER_COLOR,
};
